// StaffHome.js - Supabase monthly_shifts対応版

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "./supabase";
import dayjs from "dayjs";
import LoginTransition from "./components/LoginTransition";
import InstructionNotificationModal from "./components/InstructionNotificationModal";
import {
  BarChart3,
  Calendar,
  Clock,
  MapPin,
  ChevronRight,
  Star,
  LogIn,
  BookOpen,
  ExternalLink
} from "lucide-react";
import { useOrganization } from './contexts/OrganizationContext';

const StaffHome = () => {
  const { currentUser, currentOrganization } = useOrganization();
  
  const [thisWeekShift, setThisWeekShift] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [mySurgeries, setMySurgeries] = useState([]);
  const [myEmployeeId, setMyEmployeeId] = useState("");
  const [callerStaffId, setCallerStaffId] = useState(""); // 自分の staff.id (uuid)
  const [weeklyShiftMap, setWeeklyShiftMap] = useState({});
  const [staffName, setStaffName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [surgeryRequests, setSurgeryRequests] = useState([]);
  const [showTransition, setShowTransition] = useState(false);
  const [todayTasks, setTodayTasks] = useState([]);
  const [myComments, setMyComments] = useState([]); // 自分宛のコメント
  const [completingId, setCompletingId] = useState(null); // 完了処理中の surgery.id
  const [notifHistory, setNotifHistory] = useState([]); // 通知履歴（指導+汎用、既読/未読含む）
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState(null); // 指導通知モーダル
  const [traineeNamesById, setTraineeNamesById] = useState({});
  
  // リンク設定用state
  const [attendanceUrl, setAttendanceUrl] = useState("");
  const [manualUrl, setManualUrl] = useState("");

  // リンク設定取得（Supabase、組織ごと）
  useEffect(() => {
    if (!currentOrganization?.id) return;
    const fetchSettings = async () => {
      try {
        console.log("🔍 設定取得開始（Supabase）...");

        const { data, error } = await supabase
          .from('settings')
          .select('key, value')
          .eq('organization_id', currentOrganization.id)
          .in('key', ['attendance_url', 'manual_url']);

        if (error) {
          console.error("❌ 設定取得エラー:", error);
          return;
        }

        console.log("✅ 設定取得成功:", data);

        if (data && data.length > 0) {
          data.forEach(setting => {
            if (setting.key === 'attendance_url') {
              setAttendanceUrl(setting.value || "");
            } else if (setting.key === 'manual_url') {
              setManualUrl(setting.value || "");
            }
          });
        } else {
          console.log("⚠️ 設定データが見つかりません");
        }
      } catch (error) {
        console.error("❌ 設定取得エラー:", error);
      }
    };

    fetchSettings();
  }, [currentOrganization?.id]);

  // ユーザー情報取得
  // localStorage の supabase_user.employeeId = staff.employee_id（例: "218213"）
  // login Edge Function が staff.employee_id をレスポンスに含めている
  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        setIsLoading(true);
        
        const supabaseUser = localStorage.getItem('supabase_user');
        
        if (supabaseUser) {
          try {
            const userData = JSON.parse(supabaseUser);
            setMyEmployeeId(userData.employeeId || userData.employee_id);
            setStaffName(userData.name);
            if (userData.id) setCallerStaffId(String(userData.id));
            console.log('✅ localStorage取得成功:', userData);
            console.log('👤 myEmployeeId:', userData.employeeId || userData.employee_id);
            console.log('🆔 callerStaffId:', userData.id);
          } catch (parseError) {
            console.error('❌ localStorage解析エラー:', parseError);
          }
        } else {
          console.warn("⚠️ Supabaseユーザー情報が見つかりません");
          
          if (currentUser?.user_metadata) {
            const name = currentUser.user_metadata.full_name || '';
            const employeeId = currentUser.user_metadata.employee_id || '';
            setMyEmployeeId(employeeId);
            setStaffName(name);
            console.log('✅ OrganizationContextから取得成功:', { name, employeeId });
          }
        }
      } catch (err) {
        console.error("❌ ユーザー情報取得エラー:", err);
        setStaffName("ユーザー");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserInfo();
  }, [currentUser]);

  // 今週の日付リスト生成
  useEffect(() => {
    const today = dayjs();
    const dates = Array.from({ length: 7 }, (_, i) => today.add(i - 3, "day"));
    const formatted = dates.map(d => ({
      label: d.format("dd"),
      date: d.format("YYYY-MM-DD"),
      short: d.format("M/D"),
      dayOfWeek: d.day()
    }));
    setThisWeekShift(formatted);
    setSelectedDate(today.format("YYYY-MM-DD"));
  }, []);

  // ✅ 週次シフト取得 - Supabase monthly_shifts から取得
  useEffect(() => {
    const fetchWeeklyShift = async () => {
      if (!myEmployeeId) {
        console.log("⏸️ スキップ: myEmployeeId がありません");
        return;
      }

      const today = dayjs();
      const dates = Array.from({ length: 7 }, (_, i) => today.add(i - 3, "day"));
      const weeklyData = {};

      try {
        console.log("🔄 今週のシフト取得開始（Supabase monthly_shifts）...");
        console.log("👤 employee_id:", myEmployeeId);
        
        const startDate = dates[0].format("YYYY-MM-DD");
        const endDate = dates[dates.length - 1].format("YYYY-MM-DD");
        
        console.log("📅 日付範囲:", startDate, "～", endDate);
        
        // 🔥 シフト公開 (shift_publications) のチェック:
        //   期間に含まれる (year, month) で公開されているものだけスタッフに見せる。
        //   未公開月のシフトは "ー" にして隠す。
        const monthsInRange = new Set();
        dates.forEach((d) => {
          monthsInRange.add(`${d.year()}-${d.month() + 1}`);
        });
        const monthKeys = Array.from(monthsInRange).map((s) => s.split('-').map(Number));
        // 公開状態を取得。 失敗 or 行なしは未公開扱い (= 未公開情報が漏れないよう厳格に)。
        const { data: pubs, error: pubErr } = await supabase
          .from('shift_publications')
          .select('year, month')
          .eq('organization_id', currentOrganization?.id || '')
          .in('year', [...new Set(monthKeys.map((p) => p[0]))]);
        if (pubErr) {
          console.warn('[StaffHome] shift_publications 取得エラー:', pubErr);
        }
        const publishedSet = new Set((pubs || []).map((p) => `${p.year}-${p.month}`));
        const isMonthPublished = (d) => publishedSet.has(`${d.year()}-${d.month() + 1}`);
        console.log('[StaffHome] 公開判定:', {
          orgId: currentOrganization?.id,
          publishedMonths: Array.from(publishedSet),
        });

        // ✅ Supabase monthly_shifts テーブルから取得
        const { data: shiftData, error } = await supabase
          .from('monthly_shifts')
          .select('shift_date, shift_type')
          .eq('employee_id', myEmployeeId)
          .eq('organization_id', currentOrganization?.id || '')
          .gte('shift_date', startDate)
          .lte('shift_date', endDate);

        if (error) {
          console.error("❌ Supabase シフト取得エラー:", error);
          // エラー時はデフォルト値
          dates.forEach(d => {
            weeklyData[d.format("YYYY-MM-DD")] = "ー";
          });
          setWeeklyShiftMap(weeklyData);
          return;
        }

        // 未公開月のシフトを除外
        const filteredShiftData = (shiftData || []).filter((row) => {
          const d = dayjs(row.shift_date);
          return isMonthPublished(d);
        });
        
        console.log(`✅ シフトデータ取得: ${shiftData?.length || 0}件 (公開済み: ${filteredShiftData.length})`);

        // Phase F: 「空 = 日勤」の旧仕様を撤廃。値が入っているレコードはそのまま表示し、
        // 空 / null は「ー」（未定）として表示する。
        //   更に、公開されていない月のシフトは表示しない (filteredShiftData を使う)。
        const shiftDataMap = {};
        filteredShiftData.forEach((row) => {
          const shiftType =
            row.shift_type && row.shift_type.trim() !== ""
              ? row.shift_type
              : "ー";
          shiftDataMap[row.shift_date] = shiftType;
        });

        // 全日付分のデータを生成（レコード自体がない日も「ー」）
        dates.forEach((d) => {
          const dateStr = d.format("YYYY-MM-DD");
          weeklyData[dateStr] = shiftDataMap[dateStr] || "ー";
        });
        
        setWeeklyShiftMap(weeklyData);
        console.log("✅ 今週のシフトマップ:", weeklyData);
      } catch (err) {
        console.error("❌ 今週のシフト取得エラー:", err);
        
        dates.forEach(d => {
          weeklyData[d.format("YYYY-MM-DD")] = "ー";
        });
        setWeeklyShiftMap(weeklyData);
      }
    };

    fetchWeeklyShift();
  }, [myEmployeeId, currentOrganization]);

  // 公開済みの手術スケジュールを Supabase から取得し、自分が配置されているものだけに絞る
  // - published_at IS NOT NULL のもののみ表示
  // - scrub / circulating に自分(staff.id または employeeId) が含まれるものに絞る
  useEffect(() => {
    const fetchMySurgeries = async () => {
      if (!selectedDate || !currentOrganization?.id) return;
      if (!callerStaffId && !myEmployeeId) return;

      const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

      try {
        const { data, error } = await supabase
          .from('surgery_schedules')
          .select('*')
          .eq('organization_id', currentOrganization.id)
          .eq('date', selectedDate)
          .not('published_at', 'is', null)
          .order('start_time', { ascending: true });

        if (error) {
          console.error('❌ 手術取得エラー:', error);
          setMySurgeries([]);
          return;
        }

        const myAssigned = (data || []).filter((s) => {
          const all = [
            ...toArr(s.scrub),
            ...toArr(s.circulating),
            ...toArr(s.trainer),
            ...toArr(s.trainee),
          ];
          return all.some((p) => {
            if (!p) return false;
            if (callerStaffId && p.id && String(p.id) === callerStaffId) return true;
            if (myEmployeeId && p.employeeId && String(p.employeeId) === String(myEmployeeId)) return true;
            return false;
          });
        });

        // 互換性のため formattedDate を埋める（既存の todayTasks ロジック等で使用）
        const withDate = myAssigned.map((s) => ({
          ...s,
          docId: s.id,
          formattedDate: s.date,
          startTime: s.start_time,
          endTime: s.end_time,
        }));

        setMySurgeries(withDate);
        setSurgeryRequests([]); // 旧 surgeryRequests 系は使わない
      } catch (err) {
        console.error('❌ 手術情報読み込みエラー:', err);
        setMySurgeries([]);
        setSurgeryRequests([]);
      }
    };

    fetchMySurgeries();
  }, [selectedDate, callerStaffId, myEmployeeId, currentOrganization?.id]);

  // 配置されている手術の全コメントを取得（自分宛/その他を区別して表示）
  useEffect(() => {
    const fetchComments = async () => {
      if (!currentOrganization?.id || mySurgeries.length === 0) {
        setMyComments([]);
        return;
      }
      try {
        const surgeryIds = mySurgeries.map((s) => s.id || s.docId).filter(Boolean);
        if (surgeryIds.length === 0) {
          setMyComments([]);
          return;
        }
        const { data, error } = await supabase
          .from('surgery_staff_comments')
          .select('*')
          .eq('organization_id', currentOrganization.id)
          .in('surgery_id', surgeryIds)
          .order('created_at', { ascending: true });
        if (error) {
          console.error('❌ コメント取得エラー:', error);
          setMyComments([]);
          return;
        }
        setMyComments(data || []);
      } catch (e) {
        console.error('❌ コメント取得エラー:', e);
        setMyComments([]);
      }
    };
    fetchComments();
  }, [currentOrganization?.id, mySurgeries]);

  // 通知履歴を取得（過去30日分、指導通知 + 汎用通知をマージ）
  const loadNotifHistory = useCallback(async () => {
    if (!currentOrganization?.id || !callerStaffId) {
      setNotifHistory([]);
      return;
    }
    setHistoryLoading(true);
    try {
      const sinceIso = dayjs().subtract(30, 'day').toISOString();

      // 指導通知（自分が trainer のもの、status 問わず）
      const { data: instData, error: instErr } = await supabase
        .from('instruction_notifications')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .eq('trainer_staff_id', callerStaffId)
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: false })
        .limit(100);
      if (instErr) console.error('❌ 指導通知履歴取得エラー:', instErr);

      // 汎用通知（自分宛、read_at 問わず）
      const { data: staffData, error: staffErr } = await supabase
        .from('staff_notifications')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .eq('recipient_staff_id', callerStaffId)
        .gte('created_at', sinceIso)
        .order('created_at', { ascending: false })
        .limit(100);
      if (staffErr) console.error('❌ 汎用通知履歴取得エラー:', staffErr);

      // 育成対象名の解決
      const traineeIds = Array.from(
        new Set((instData || []).map((n) => n.trainee_staff_id))
      ).filter(Boolean);
      if (traineeIds.length > 0) {
        const { data: traineeRows } = await supabase
          .from('staff')
          .select('id, last_name, first_name')
          .in('id', traineeIds);
        const map = {};
        (traineeRows || []).forEach((s) => {
          map[s.id] = `${s.last_name || ''} ${s.first_name || ''}`.trim() || s.id;
        });
        setTraineeNamesById(map);
      }

      const merged = [
        ...(instData || []).map((n) => ({
          key: `inst-${n.id}`,
          kind: 'instruction',
          n,
          ts: n.created_at,
          unread: n.status === 'pending',
        })),
        ...(staffData || []).map((n) => ({
          key: `staff-${n.id}`,
          kind: 'staff',
          n,
          ts: n.created_at,
          unread: !n.read_at,
        })),
      ].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));

      setNotifHistory(merged);
    } catch (e) {
      console.error('❌ 通知履歴取得エラー:', e);
      setNotifHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [currentOrganization?.id, callerStaffId]);

  useEffect(() => {
    loadNotifHistory();
  }, [loadNotifHistory]);

  // 汎用通知を既読化
  const markStaffNotifReadHome = async (notifId) => {
    try {
      const { error } = await supabase
        .from('staff_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notifId)
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setNotifHistory((prev) =>
        prev.map((item) =>
          item.kind === 'staff' && item.n.id === notifId
            ? { ...item, n: { ...item.n, read_at: new Date().toISOString() }, unread: false }
            : item
        )
      );
    } catch (e) {
      console.error('❌ 既読化失敗:', e);
    }
  };

  // 配置完了ハンドラ：complete-surgery Edge Function を呼ぶ
  const handleCompleteSurgery = async (surgeryId) => {
    if (!surgeryId || !currentOrganization?.id) return;
    if (!window.confirm('この手術を「完了」にしますか？指導症例の通知が生成されます。')) return;
    setCompletingId(surgeryId);
    try {
      const { data, error } = await supabase.functions.invoke('complete-surgery', {
        body: { surgeryId, organizationId: currentOrganization.id },
      });
      if (error) throw error;
      if (data?.success === false) {
        alert(`完了処理に失敗しました: ${data.message || '不明なエラー'}`);
        return;
      }
      // ローカル state を更新（再フェッチの代わり）
      setMySurgeries((prev) =>
        prev.map((s) =>
          (s.id || s.docId) === surgeryId
            ? { ...s, completed_at: data?.completed_at || new Date().toISOString() }
            : s
        )
      );
    } catch (e) {
      console.error('❌ 完了処理失敗:', e);
      alert(`完了処理に失敗しました: ${e.message}`);
    } finally {
      setCompletingId(null);
    }
  };

  // surgery_id → 自分宛コメント一覧 のマップ
  const commentsBySurgeryId = useMemo(() => {
    const m = {};
    myComments.forEach((c) => {
      const key = c.surgery_id;
      if (!m[key]) m[key] = [];
      m[key].push(c);
    });
    return m;
  }, [myComments]);

  // 自分のロール表示用：器械出し / 外回り
  const getMyRoleLabels = (surgery) => {
    const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    const labels = [];
    const isMe = (p) => {
      if (!p) return false;
      if (callerStaffId && p.id && String(p.id) === callerStaffId) return true;
      if (myEmployeeId && p.employeeId && String(p.employeeId) === String(myEmployeeId)) return true;
      return false;
    };
    if (toArr(surgery.scrub).some(isMe)) labels.push('器械出し');
    if (toArr(surgery.circulating).some(isMe)) labels.push('外回り');
    if (toArr(surgery.trainer).some(isMe)) labels.push('指導者');
    if (toArr(surgery.trainee).some(isMe)) labels.push('育成');
    return labels;
  };

  // 配置者の名前一覧を取得（表示用）
  const formatAssignees = (val) => {
    const toArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    return toArr(val)
      .map((p) =>
        p?.name
          || [p?.lastName, p?.firstName].filter(Boolean).join(' ')
          || [p?.last_name, p?.first_name].filter(Boolean).join(' ')
          || ''
      )
      .filter(Boolean)
      .join('、');
  };

  // 今日のタスクを計算
  useEffect(() => {
    if (!myEmployeeId) return;
    
    const today = dayjs().format("YYYY-MM-DD");
    const tasks = [];
    
    const todaySurgeries = mySurgeries.filter(s => s.formattedDate === today);
    todaySurgeries.forEach(surgery => {
      const role = surgery.scrub?.id === myEmployeeId ? "器械出し" :
                  surgery.circulating?.id === myEmployeeId ? "外回り" :
                  surgery.scrubInstructor?.id === myEmployeeId ? "器械指導" :
                  surgery.circulatingInstructor?.id === myEmployeeId ? "外回指導" : "";
      
      if (role) {
        tasks.push(`${surgery.startTime || "時間未定"} - ${surgery.department} ${surgery.procedure} (${role})`);
      }
    });
    
    const todayRequests = surgeryRequests.filter(req => req.date === today);
    todayRequests.forEach(req => {
      const myAssignment = req.assignedStaff?.find(staff => staff.staffId === myEmployeeId);
      if (myAssignment) {
        const roleText = myAssignment.role === "scrub" ? "器械出し" : "外回り";
        const instructorText = myAssignment.isInstructor ? "指導者" : "";
        tasks.push(`${req.startTime || "時間未定"} - ${req.departmentName} ${req.procedureName} (${instructorText}${roleText})`);
      }
    });
    
    const todayShift = weeklyShiftMap[today];
    if (todayShift === "/") {
      tasks.push("今日は夜勤です。体調管理に気をつけましょう");
    } else if (todayShift === "◯" && tasks.length === 0) {
      tasks.push("今日は日勤です。手術配置をご確認ください");
    } else if (todayShift === "休") {
      tasks.push("今日はお休みです。ゆっくり休んでください");
    }
    
    setTodayTasks(tasks);
  }, [myEmployeeId, mySurgeries, surgeryRequests, weeklyShiftMap]);

  // ボタンクリックハンドラー
  const handleAttendanceClick = () => {
    if (attendanceUrl) {
      window.open(attendanceUrl, '_blank');
    } else {
      alert('出勤退勤システムのURLが設定されていません。\n管理者に設定を依頼してください。');
    }
  };

  const handleManualClick = () => {
    if (manualUrl) {
      window.open(manualUrl, '_blank');
    } else {
      alert('手順書のURLが設定されていません。\n管理者に設定を依頼してください。');
    }
  };

  // ヘルパー関数
  const getWeekdayLabel = (dayOfWeek) => {
    const labels = ["日", "月", "火", "水", "木", "金", "土"];
    return labels[dayOfWeek];
  };

  const getWeekdayColor = (dayOfWeek) => {
    if (dayOfWeek === 0) return "#E57373";
    if (dayOfWeek === 6) return "#64B5F6";
    return "#424242";
  };

  const getShiftTypeColor = (shiftType) => {
    const colorMap = {
      "◯": "#81C784",    // 日勤 - 緑
      "": "#81C784",      // 空文字も日勤
      "/": "#E57373",     // 夜勤 - 赤
      "休": "#FFB74D",    // 休み - オレンジ
      "ー": "#BDBDBD",    // 未定 - グレー
    };
    // /3 等の夜勤系
    if (shiftType && shiftType.startsWith("/")) return "#E57373";
    // X] 等の明け系
    if (shiftType && shiftType.startsWith("X")) return "#CE93D8";
    // オ系（オンコール等）
    if (shiftType && shiftType.startsWith("オ")) return "#4FC3F7";
    // □系
    if (shiftType && shiftType.startsWith("□")) return "#90A4AE";
    return colorMap[shiftType] || "#9575CD";
  };

  const getShiftTypeLabel = (shiftType) => {
    const labelMap = {
      "◯": "日勤",
      "": "日勤",
      "/": "夜勤",
      "休": "休み",
      "ー": "未定",
    };
    return labelMap[shiftType] || shiftType;
  };

  const formatSurgeryTime = (startTime, endTime) => {
    if (!startTime) return "時間未定";
    if (!endTime) return `${startTime}～`;
    return `${startTime}～${endTime}`;
  };

  // スタイル定義
  const styles = {
    container: {
      padding: "1rem",
      fontFamily: "'Noto Sans JP', sans-serif",
      maxWidth: "600px",
      margin: "0 auto",
      color: "#37474F",
      backgroundColor: "#FAFAFA",
      minHeight: "100vh",
    },
    greeting: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "white",
      padding: "1rem 1.5rem",
      borderRadius: "12px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
      marginBottom: "2rem",
    },
    greetingText: {
      fontSize: "1.1rem",
      fontWeight: "600",
    },
    staffName: {
      color: "#1976D2",
    },
    section: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "1.5rem",
      marginBottom: "2rem",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
    },
    sectionHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "1.2rem",
      paddingBottom: "0.8rem",
      borderBottom: "1px solid #EEEEEE",
    },
    sectionTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      color: "#37474F",
      margin: 0,
    },
    weekShiftContainer: {
      display: "flex",
      overflowX: "auto",
      gap: "0.5rem",
      paddingBottom: "0.5rem",
    },
    dayCard: (isSelected, dayOfWeek) => ({
      minWidth: "70px",
      padding: "0.75rem 0.5rem",
      backgroundColor: isSelected ? "#E3F2FD" : "#FAFAFA",
      border: `1px solid ${isSelected ? "#64B5F6" : "#E0E0E0"}`,
      borderRadius: "10px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s ease",
      transform: isSelected ? "translateY(-2px)" : "none",
      boxShadow: isSelected ? "0 4px 8px rgba(0, 0, 0, 0.1)" : "none",
    }),
    dayOfWeek: (dayOfWeek) => ({
      color: getWeekdayColor(dayOfWeek),
      fontWeight: "600",
      fontSize: "1rem",
    }),
    dayDate: {
      fontSize: "0.8rem",
      color: "#78909C",
      marginTop: "0.25rem",
    },
    shiftBadge: (shiftType) => ({
      backgroundColor: getShiftTypeColor(shiftType),
      color: "white",
      padding: "0.25rem 0.5rem",
      borderRadius: "4px",
      fontWeight: "600",
      fontSize: "0.8rem",
      marginTop: "0.5rem",
      display: "inline-block",
    }),
    noSurgeries: {
      padding: "2rem 1rem",
      textAlign: "center",
      backgroundColor: "#FAFAFA",
      borderRadius: "8px",
      color: "#78909C",
    },
    surgeryCard: {
      backgroundColor: "#FFFFFF",
      border: "1px solid #EEEEEE",
      borderRadius: "10px",
      padding: "1.25rem",
      marginBottom: "1rem",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    surgeryDepartment: {
      fontWeight: "700",
      color: "#37474F",
      fontSize: "1.1rem",
    },
    surgeryProcedure: {
      fontSize: "0.9rem",
      color: "#546E7A",
    },
    surgeryTime: {
      fontSize: "0.85rem",
      color: "#1976D2",
      backgroundColor: "#E3F2FD",
      padding: "0.2rem 0.5rem",
      borderRadius: "4px",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      marginTop: "0.4rem",
    },
    surgeryInfo: {
      display: "grid",
      gridTemplateColumns: "auto 1fr",
      gap: "0.75rem 1rem",
      alignItems: "center",
      fontSize: "0.95rem",
    },
    surgeryRole: {
      padding: "0.5rem",
      borderRadius: "6px",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    surgeryIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "24px",
      height: "24px",
      backgroundColor: "#E0E0E0",
      color: "#424242",
      borderRadius: "50%",
      fontSize: "0.8rem",
    },
    surgeryBadge: {
      display: "inline-block",
      padding: "0.2rem 0.5rem",
      borderRadius: "12px",
      fontSize: "0.75rem",
      fontWeight: "600",
      marginLeft: "0.5rem",
      backgroundColor: "#FF9800",
      color: "white",
    },
    shimmer: {
      background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 1.5s infinite",
      borderRadius: "8px",
      width: "100%",
      height: "24px",
      margin: "0.5rem 0",
    },
    patientInfo: {
      backgroundColor: "#F5F5F5",
      borderRadius: "8px",
      padding: "0.8rem",
      marginTop: "0.8rem",
      fontSize: "0.9rem",
    },
    roomBadge: {
      backgroundColor: "#4CAF50",
      color: "white",
      padding: "0.15rem 0.4rem",
      borderRadius: "4px",
      fontSize: "0.75rem",
      fontWeight: "600",
      marginRight: "0.5rem",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.2rem",
    },
    divider: {
      height: "1px",
      backgroundColor: "#EEEEEE",
      margin: "1rem 0",
    },
    surgeryCardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "0.75rem",
    },
    myRoleBadge: (isInstructor) => ({
      backgroundColor: isInstructor ? "#9C27B0" : "#2196F3",
      color: "white",
      padding: "0.25rem 0.5rem",
      borderRadius: "4px",
      fontSize: "0.75rem",
      fontWeight: "600",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
      marginTop: "0.3rem",
    }),
    utilityButtonsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "1rem",
      marginBottom: "2rem",
    },
    utilityButton: {
      padding: "1.2rem",
      borderRadius: "16px",
      border: "none",
      cursor: "pointer",
      transition: "all 0.3s ease",
      textDecoration: "none",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.5rem",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    },
    attendanceButton: {
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      color: "white",
    },
    manualButton: {
      background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      color: "white",
    },
    // === Phase E-5: 配置/コメント/完了ボタン ===
    assigneeBlock: {
      marginTop: "0.75rem",
      paddingTop: "0.75rem",
      borderTop: "1px dashed #E0E0E0",
      display: "flex",
      flexDirection: "column",
      gap: "0.35rem",
    },
    assigneeRow: {
      display: "flex",
      alignItems: "baseline",
      gap: "0.6rem",
      fontSize: "0.9rem",
    },
    assigneeLabel: {
      minWidth: "60px",
      color: "#78909C",
      fontSize: "0.8rem",
      fontWeight: 600,
    },
    assigneeValue: {
      color: "#37474F",
      flex: 1,
    },
    myCommentsBlock: {
      marginTop: "0.75rem",
      padding: "0.75rem",
      backgroundColor: "#FFF8E1",
      borderRadius: "8px",
      border: "1px solid #FFE082",
    },
    myCommentsTitle: {
      fontSize: "0.85rem",
      fontWeight: 700,
      color: "#F57F17",
      marginBottom: "0.5rem",
    },
    myCommentItem: {
      padding: "0.5rem 0.5rem",
      borderTop: "1px dotted #FFE082",
    },
    myCommentItemHighlighted: {
      backgroundColor: "#FFE0B2",
      borderRadius: "6px",
      marginTop: "0.25rem",
      padding: "0.5rem",
      borderTop: "none",
      boxShadow: "0 1px 3px rgba(255, 152, 0, 0.2)",
    },
    myCommentSelfBadge: {
      display: "inline-block",
      marginLeft: "0.4rem",
      padding: "0.05rem 0.4rem",
      borderRadius: "4px",
      backgroundColor: "#E65100",
      color: "#fff",
      fontSize: "0.7rem",
      fontWeight: 700,
    },
    myCommentTargets: {
      fontSize: "0.75rem",
      color: "#1976D2",
      marginBottom: "0.2rem",
    },
    myCommentMeta: {
      display: "flex",
      justifyContent: "space-between",
      fontSize: "0.75rem",
      color: "#7E5E1E",
      marginBottom: "0.2rem",
    },
    myCommentBody: {
      fontSize: "0.9rem",
      color: "#37474F",
      whiteSpace: "pre-wrap",
      lineHeight: 1.5,
    },
    completeButton: {
      padding: "0.6rem 1.2rem",
      backgroundColor: "#43A047",
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      fontSize: "0.95rem",
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 2px 6px rgba(67, 160, 71, 0.3)",
    },
    // === 通知履歴セクション ===
    notifReloadBtn: {
      background: "transparent",
      border: "none",
      cursor: "pointer",
      fontSize: "1rem",
      color: "#6b7280",
      padding: "0.25rem 0.5rem",
    },
    notifList: {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
      maxHeight: "400px",
      overflowY: "auto",
    },
    notifHistoryItem: {
      width: "100%",
      textAlign: "left",
      background: "#FAFAFA",
      border: "1px solid #EEEEEE",
      borderRadius: "8px",
      padding: "0.7rem 0.9rem",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      gap: "0.25rem",
      transition: "background-color 0.2s ease",
    },
    notifHistoryItemUnread: {
      background: "#FFF8E1",
      borderColor: "#FFE082",
    },
    notifHistoryRow: {
      display: "flex",
      alignItems: "center",
      gap: "0.4rem",
    },
    notifHistoryDate: {
      fontSize: "0.75rem",
      color: "#9E9E9E",
      marginLeft: "auto",
    },
    notifHistoryTitle: {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: "#37474F",
    },
    notifHistoryMeta: {
      fontSize: "0.8rem",
      color: "#78909C",
    },
    unreadDot: {
      color: "#E65100",
      fontSize: "0.7rem",
    },
    statusBadgeDone: {
      display: "inline-block",
      marginLeft: "0.4rem",
      padding: "0.05rem 0.4rem",
      borderRadius: "4px",
      backgroundColor: "#9E9E9E",
      color: "#fff",
      fontSize: "0.7rem",
      fontWeight: 700,
    },
    // === 通知ベルと同じカラーの種別タグ（履歴で再利用） ===
    kindTagInstruction: {
      display: "inline-block",
      padding: "1px 6px",
      borderRadius: "4px",
      fontSize: "0.7rem",
      fontWeight: 700,
      background: "#fce4ec",
      color: "#ad1457",
    },
    kindTagAssignment: {
      display: "inline-block",
      padding: "1px 6px",
      borderRadius: "4px",
      fontSize: "0.7rem",
      fontWeight: 700,
      background: "#fff3e0",
      color: "#e65100",
    },
  };

  // LoginTransitionを表示
  if (showTransition && staffName) {
    return (
      <LoginTransition 
        staffName={staffName}
        todayTasks={todayTasks}
        onComplete={() => setShowTransition(false)}
      />
    );
  }

  // ローディング中の表示
  if (isLoading) {
    return (
      <div style={styles.container}>        
        <div style={styles.section}>
          <div style={styles.shimmer}></div>
          <div style={{...styles.shimmer, width: '80%'}}></div>
          <div style={{...styles.shimmer, width: '60%'}}></div>
        </div>
        <div style={styles.section}>
          <div style={styles.shimmer}></div>
          <div style={{...styles.shimmer, width: '90%'}}></div>
          <div style={{...styles.shimmer, width: '70%'}}></div>
        </div>
      </div>
    );
  }

  // 手術データと申込データを日付でマージ
  const allSurgeries = [
    ...mySurgeries,
    ...surgeryRequests.filter(req => 
      !mySurgeries.some(surgery => surgery.id === req.id)
    )
  ];

  const sortedSurgeries = [...allSurgeries].sort((a, b) => {
    if (!a.startTime) return 1;
    if (!b.startTime) return -1;
    return a.startTime.localeCompare(b.startTime);
  });

  return (
    <div style={styles.container}>
      {/* グリーティング */}
      <div style={styles.greeting}>
        <div style={styles.greetingText}>
          こんにちは <span style={styles.staffName}>{staffName}</span> さん
        </div>
        <div style={{fontSize: "0.85rem", color: "#78909C"}}>
          {dayjs().format("YYYY年M月D日")}
        </div>
      </div>

      {/* 出勤退勤・手順書ボタン */}
      <div style={styles.utilityButtonsGrid}>
        <button 
          onClick={handleAttendanceClick}
          style={{
            ...styles.utilityButton,
            ...styles.attendanceButton,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <LogIn size={24} />
          </div>
          <div style={{ fontSize: "1rem", fontWeight: "700" }}>出勤退勤</div>
          <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>
            <ExternalLink size={12} style={{ display: "inline", marginRight: "4px" }} />
            タイムカード
          </div>
        </button>

        <button 
          onClick={handleManualClick}
          style={{
            ...styles.utilityButton,
            ...styles.manualButton,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(240, 147, 251, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div style={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <BookOpen size={24} />
          </div>
          <div style={{ fontSize: "1rem", fontWeight: "700" }}>手順書</div>
          <div style={{ fontSize: "0.75rem", opacity: 0.9 }}>
            <ExternalLink size={12} style={{ display: "inline", marginRight: "4px" }} />
            マニュアル
          </div>
        </button>
      </div>

      {/* 通知履歴 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>お知らせ</h2>
          <button
            onClick={loadNotifHistory}
            style={styles.notifReloadBtn}
            title="再取得"
          >
            ↻
          </button>
        </div>
        {historyLoading ? (
          <div style={styles.noSurgeries}>
            <p style={{ margin: 0 }}>読み込み中...</p>
          </div>
        ) : notifHistory.length === 0 ? (
          <div style={styles.noSurgeries}>
            <p style={{ margin: 0 }}>過去30日以内のお知らせはありません</p>
          </div>
        ) : (
          <div style={styles.notifList}>
            {notifHistory.map((item) => {
              const { kind, n, unread } = item;
              if (kind === 'instruction') {
                const traineeName =
                  traineeNamesById[n.trainee_staff_id]
                  || `(社員番号 ${n.trainee_employee_id || '不明'})`;
                const roleLabel = n.role === 'scrub' ? '器械出し' : '外回り';
                const procCount = Array.isArray(n.procedures) ? n.procedures.length : 0;
                return (
                  <button
                    key={item.key}
                    onClick={() => setSelectedInstruction(n)}
                    style={{
                      ...styles.notifHistoryItem,
                      ...(unread ? styles.notifHistoryItemUnread : {}),
                    }}
                  >
                    <div style={styles.notifHistoryRow}>
                      <span style={styles.kindTagInstruction}>指導</span>
                      {unread && <span style={styles.unreadDot}>●</span>}
                      <span style={styles.notifHistoryDate}>
                        {n.created_at ? dayjs(n.created_at).format('M/D HH:mm') : ''}
                      </span>
                    </div>
                    <div style={styles.notifHistoryTitle}>
                      {n.surgery_procedure_name || '手術'} （{roleLabel} / 育成対象: {traineeName}）
                    </div>
                    <div style={styles.notifHistoryMeta}>
                      対象術式 {procCount} 件 · 手術日 {n.surgery_date}
                      {n.status === 'completed' && (
                        <span style={styles.statusBadgeDone}>記入済</span>
                      )}
                    </div>
                  </button>
                );
              }
              // 汎用通知
              return (
                <button
                  key={item.key}
                  onClick={() => unread && markStaffNotifReadHome(n.id)}
                  style={{
                    ...styles.notifHistoryItem,
                    ...(unread ? styles.notifHistoryItemUnread : {}),
                  }}
                >
                  <div style={styles.notifHistoryRow}>
                    <span style={styles.kindTagAssignment}>配置</span>
                    {unread && <span style={styles.unreadDot}>●</span>}
                    <span style={styles.notifHistoryDate}>
                      {n.created_at ? dayjs(n.created_at).format('M/D HH:mm') : ''}
                    </span>
                  </div>
                  <div style={styles.notifHistoryTitle}>{n.title}</div>
                  {n.body && (
                    <div style={styles.notifHistoryMeta}>{n.body}</div>
                  )}
                  {!unread && (
                    <div style={styles.notifHistoryMeta}>
                      <span style={styles.statusBadgeDone}>既読</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 指導通知モーダル（履歴からの起動用） */}
      {selectedInstruction && (
        <InstructionNotificationModal
          notification={selectedInstruction}
          traineeName={traineeNamesById[selectedInstruction.trainee_staff_id]}
          onClose={() => {
            setSelectedInstruction(null);
            loadNotifHistory();
          }}
        />
      )}

      {/* 今週のシフト */}
      <div 
        style={styles.section}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "none";
          e.currentTarget.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.05)";
        }}
      >
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>今週のシフト</h2>
        </div>
        
        <div style={styles.weekShiftContainer}>
          {thisWeekShift.map((item, index) => (
            <div
              key={index}
              onClick={() => setSelectedDate(item.date)}
              style={styles.dayCard(item.date === selectedDate, item.dayOfWeek)}
              onMouseEnter={(e) => {
                if (item.date !== selectedDate) {
                  e.currentTarget.style.backgroundColor = "#F5F5F5";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }
              }}
              onMouseLeave={(e) => {
                if (item.date !== selectedDate) {
                  e.currentTarget.style.backgroundColor = "#FAFAFA";
                  e.currentTarget.style.transform = "none";
                }
              }}
            >
              <div style={styles.dayOfWeek(item.dayOfWeek)}>
                {getWeekdayLabel(item.dayOfWeek)}
              </div>
              <div style={styles.dayDate}>{item.short}</div>
              <div style={styles.shiftBadge(weeklyShiftMap[item.date] || "ー")}>
                {getShiftTypeLabel(weeklyShiftMap[item.date] || "ー")}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 当日の手術配置 */}
      <div 
        style={styles.section}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "none";
          e.currentTarget.style.boxShadow = "0 2px 10px rgba(0, 0, 0, 0.05)";
        }}
      >
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>手術配置</h2>
          <div style={{fontSize: "0.9rem", color: "#546E7A"}}>
            {dayjs(selectedDate).format("M月D日")} ({getWeekdayLabel(dayjs(selectedDate).day())})
            {sortedSurgeries.length > 0 && <span style={{fontWeight: "bold", marginLeft: "0.5rem"}}>{sortedSurgeries.length}件</span>}
          </div>
        </div>

        {sortedSurgeries.length === 0 ? (
          <div style={styles.noSurgeries}>
            <p style={{margin: 0}}>この日に割り当てられている手術はありません</p>
          </div>
        ) : (
          sortedSurgeries.map((surgery, index) => {
            const surgeryId = surgery.id || surgery.docId;
            const isCompleted = !!surgery.completed_at;
            const myRoles = getMyRoleLabels(surgery);
            const scrubNames = formatAssignees(surgery.scrub);
            const circNames = formatAssignees(surgery.circulating);
            const trainerNames = formatAssignees(surgery.trainer);
            const traineeNames = formatAssignees(surgery.trainee);
            const myCommentList = commentsBySurgeryId[surgeryId] || [];

            return (
              <div key={surgeryId || index} style={{
                ...styles.surgeryCard,
                borderLeft: isCompleted ? '4px solid #9E9E9E' : '4px solid #2196F3',
                opacity: isCompleted ? 0.85 : 1,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}>
                <div style={styles.surgeryCardHeader}>
                  <div>
                    <div style={styles.surgeryDepartment}>
                      {surgery.department || "不明"}
                      {isCompleted && <span style={{...styles.surgeryBadge, backgroundColor: '#9E9E9E'}}>完了済</span>}
                    </div>
                    <div style={styles.surgeryProcedure}>{surgery.procedure || "不明"}</div>
                    <div style={styles.surgeryTime}>
                      <Clock size={14} />
                      {formatSurgeryTime(surgery.startTime, surgery.endTime)}
                    </div>
                  </div>

                  <div style={{textAlign: 'right'}}>
                    {surgery.room && (
                      <span style={styles.roomBadge}>
                        <MapPin size={12} />
                        {surgery.room}
                      </span>
                    )}
                    {myRoles.length > 0 && (
                      <div style={styles.myRoleBadge(myRoles.includes('指導者'))}>
                        {myRoles.includes('指導者') && <Star size={12} />}
                        {myRoles.join(' / ')}
                      </div>
                    )}
                  </div>
                </div>

                {/* 配置一覧 */}
                <div style={styles.assigneeBlock}>
                  {scrubNames && (
                    <div style={styles.assigneeRow}>
                      <span style={styles.assigneeLabel}>器械出し</span>
                      <span style={styles.assigneeValue}>{scrubNames}</span>
                    </div>
                  )}
                  {circNames && (
                    <div style={styles.assigneeRow}>
                      <span style={styles.assigneeLabel}>外回り</span>
                      <span style={styles.assigneeValue}>{circNames}</span>
                    </div>
                  )}
                  {trainerNames && (
                    <div style={styles.assigneeRow}>
                      <span style={styles.assigneeLabel}>指導</span>
                      <span style={styles.assigneeValue}>{trainerNames}</span>
                    </div>
                  )}
                  {traineeNames && (
                    <div style={styles.assigneeRow}>
                      <span style={styles.assigneeLabel}>育成</span>
                      <span style={styles.assigneeValue}>{traineeNames}</span>
                    </div>
                  )}
                </div>

                {/* コメント一覧（自分宛は強調表示） */}
                {myCommentList.length > 0 && (
                  <div style={styles.myCommentsBlock}>
                    <div style={styles.myCommentsTitle}>💬 コメント</div>
                    {myCommentList.map((c) => {
                      const targetIds = Array.isArray(c.target_staff_ids)
                        ? c.target_staff_ids.map(String)
                        : [];
                      const isForMe = callerStaffId && targetIds.includes(callerStaffId);
                      return (
                        <div
                          key={c.id}
                          style={{
                            ...styles.myCommentItem,
                            ...(isForMe ? styles.myCommentItemHighlighted : {}),
                          }}
                        >
                          <div style={styles.myCommentMeta}>
                            <span>
                              {c.author_name || '（投稿者不明）'}
                              {isForMe && (
                                <span style={styles.myCommentSelfBadge}>あなた宛</span>
                              )}
                            </span>
                            <span style={{ color: '#9E9E9E' }}>
                              {c.created_at ? dayjs(c.created_at).format('M/D HH:mm') : ''}
                            </span>
                          </div>
                          {!isForMe && targetIds.length > 0 && (
                            <div style={styles.myCommentTargets}>
                              宛先:{' '}
                              {targetIds
                                .map((tid) => {
                                  // assigned からの名前解決を試みる
                                  const toArr = (v) =>
                                    Array.isArray(v) ? v : v ? [v] : [];
                                  const all = [
                                    ...toArr(surgery.scrub),
                                    ...toArr(surgery.circulating),
                                    ...toArr(surgery.trainer),
                                    ...toArr(surgery.trainee),
                                  ];
                                  const hit = all.find((p) => String(p?.id) === tid);
                                  return (
                                    hit?.name
                                    || [hit?.lastName, hit?.firstName].filter(Boolean).join(' ')
                                    || [hit?.last_name, hit?.first_name].filter(Boolean).join(' ')
                                    || `(ID:${tid.slice(0, 8)})`
                                  );
                                })
                                .join('、')}
                            </div>
                          )}
                          <div style={styles.myCommentBody}>{c.content}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 完了ボタン */}
                {!isCompleted && myRoles.length > 0 && (
                  <div style={{marginTop: '1rem', textAlign: 'right'}}>
                    <button
                      onClick={() => handleCompleteSurgery(surgeryId)}
                      disabled={completingId === surgeryId}
                      style={styles.completeButton}
                    >
                      {completingId === surgeryId ? '完了処理中...' : '✓ この手術を完了にする'}
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* アクションボタン */}
      <div style={{ marginTop: "2rem" }}>
        <Link 
          to="/skills" 
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            width: "100%",
            padding: "1.2rem 1.5rem",
            marginBottom: "1.5rem",
            textDecoration: "none",
            borderRadius: "16px",
            background: "linear-gradient(135deg, #00C9FF 0%, #92FE9D 100%)",
            color: "#FFFFFF",
            fontWeight: "600",
            fontSize: "1.1rem",
            boxShadow: "0 10px 20px rgba(0, 201, 255, 0.2), 0 6px 6px rgba(0, 201, 255, 0.1), inset 0 -3px 0 rgba(0, 0, 0, 0.1)",
            border: "none",
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 15px 30px rgba(0, 201, 255, 0.25), 0 10px 10px rgba(0, 201, 255, 0.15), inset 0 -3px 0 rgba(0, 0, 0, 0.1)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 10px 20px rgba(0, 201, 255, 0.2), 0 6px 6px rgba(0, 201, 255, 0.1), inset 0 -3px 0 rgba(0, 0, 0, 0.1)";
          }}
        >
          <div style={{
            width: "50px",
            height: "50px",
            borderRadius: "14px",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "1.2rem",
            boxShadow: "inset 0 2px 5px rgba(255, 255, 255, 0.5), inset 0 -2px 5px rgba(0, 0, 0, 0.1)"
          }}>
            <BarChart3 size={24} color="white" />
          </div>
          <div>
            <div style={{ fontSize: "1.2rem", fontWeight: "700" }}>スキルチャート</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.95, marginTop: "0.2rem" }}>自分の習熟度を確認する</div>
          </div>
          <div style={{
            position: "absolute",
            top: "50%",
            right: "1.5rem",
            transform: "translateY(-50%)",
          }}>
            <ChevronRight size={24} color="white" />
          </div>
        </Link>

        <Link 
          to="/form" 
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            padding: "1.5rem",
            marginBottom: "1rem",
            textDecoration: "none",
            borderRadius: "20px",
            background: "linear-gradient(120deg, #3F51B5, #5C6BC0, #7986CB, #9FA8DA)",
            backgroundSize: "300% 300%",
            color: "#FFFFFF",
            fontWeight: "600",
            fontSize: "1.2rem",
            border: "none",
            position: "relative",
            overflow: "hidden",
            cursor: "pointer",
            transition: "all 0.4s ease",
            boxShadow: "0 6px 12px rgba(63, 81, 181, 0.2), 0 -2px 0 rgba(255, 255, 255, 0.15) inset",
            animation: "gradientShift 4s ease infinite",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 12px 24px rgba(63, 81, 181, 0.3), 0 -2px 0 rgba(255, 255, 255, 0.15) inset";
            e.currentTarget.style.animationPlayState = "paused";
            e.currentTarget.style.backgroundPosition = "right center";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 6px 12px rgba(63, 81, 181, 0.2), 0 -2px 0 rgba(255, 255, 255, 0.15) inset";
            e.currentTarget.style.animationPlayState = "running";
          }}
        >
          <div style={{
            width: "60px",
            height: "60px",
            borderRadius: "18px",
            backgroundColor: "rgba(255, 255, 255, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "1.2rem",
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1), inset 0 2px 3px rgba(255, 255, 255, 0.2)"
          }}>
            <Calendar size={28} color="white" />
          </div>
          <div style={{ textAlign: "left" }}>
            <div style={{ fontSize: "1.3rem", fontWeight: "700", letterSpacing: "0.5px" }}>
              シフト希望を提出する
            </div>
            <div style={{ fontSize: "0.85rem", opacity: 0.9, marginTop: "0.3rem" }}>
              来月の勤務希望を登録しましょう
            </div>
          </div>
        </Link>
      </div>

      <style>
        {`
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}
      </style>
    </div>
  );
};

export default StaffHome;